/*! blogutility-wet-egress-selector.js v1.0
   Wet-Location Emergency Egress — Spec Selector
   - No deps. Namespaced. Idempotent.
   - Renders into any <div data-wet-egress-tool> element.
   - Non-transactional: provides guidance only; no product links.
   - Accessibility: labels, aria-live for result, keyboard focus states.
*/
(function(){
  "use strict";

  const NS = "wlet";
  const SELECTOR = "[data-wet-egress-tool]";
  if (window.__WL_ET_INIT__) return; // idempotent across multiple includes
  window.__WL_ET_INIT__ = true;

  // Minimal component CSS (scoped classnames)
  const styles = `
  .${NS}-wrap{font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial; color:#0f172a}
  .${NS}-card{border:1px solid #e5e7eb;border-radius:12px;padding:16px;background:#fff}
  .${NS}-header{display:flex;align-items:center;gap:.5rem;margin:0 0 10px;font-weight:700}
  .${NS}-header .${NS}-icon{font-size:16px;color:#0ea5e9}
  .${NS}-sub{margin:0 0 12px;color:#475569;font-size:14px}
  .${NS}-form{display:grid;gap:12px;grid-template-columns:1fr}
  .${NS}-row{display:grid;gap:10px;grid-template-columns:1fr minmax(180px,240px)}
  .${NS}-row label{font-weight:600}
  .${NS}-row .${NS}-field{justify-self:end}
  .${NS}-hint{color:#64748b;font-size:12px;margin-top:4px}
  .${NS}-actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:6px}
  .${NS}-btn{border:1px solid #cbd5e1;border-radius:999px;background:#0f172a;color:#fff;padding:.5rem .8rem;font-weight:600;font-size:14px;cursor:pointer}
  .${NS}-btn.secondary{background:#fff;color:#0f172a}
  .${NS}-btn:focus{outline:2px solid #2563eb;outline-offset:2px}
  .${NS}-results{margin-top:14px;border-top:1px dashed #e5e7eb;padding-top:14px}
  .${NS}-pill{display:inline-flex;align-items:center;gap:.4rem;padding:.35rem .6rem;border-radius:999px;border:1px solid #cbd5e1;background:#f8fafc;color:#0f172a;font-size:12px;margin:2px}
  .${NS}-list{margin:10px 0 0;padding-left:18px}
  .${NS}-muted{color:#64748b}
  .${NS}-sr{position:absolute!important;height:1px;width:1px;overflow:hidden;clip:rect(1px,1px,1px,1px);white-space:nowrap}
  @media (max-width:720px){ .${NS}-row{grid-template-columns:1fr} .${NS}-row .${NS}-field{justify-self:start} }
  `;

  // Inject CSS once
  function injectCSS(){
    if (document.getElementById(NS+"-styles")) return;
    const s = document.createElement("style");
    s.id = NS+"-styles";
    s.textContent = styles;
    document.head.appendChild(s);
  }

  function el(tag, cls, html){
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  // Build the widget UI
  function build(root){
    const wrap = el("div", NS+"-wrap");
    const card = el("div", NS+"-card");
    wrap.appendChild(card);

    const h = el("h3", NS+"-header", `<span class="fa-solid fa-droplet ${NS}-icon" aria-hidden="true"></span> Wet‑Location Spec Selector`);
    const sub = el("p", NS+"-sub", "Answer a few quick questions to get a compliance‑oriented starting point (Wet vs Damp, NEMA 4/4X, IP65/IP66, cold‑weather, and testing). This is guidance only—always verify with your AHJ and manufacturer data.");
    card.appendChild(h);
    card.appendChild(sub);

    // Form
    const form = el("form", NS+"-form");
    form.setAttribute("novalidate","true");
    form.innerHTML = `
      <div class="${NS}-row">
        <label for="${NS}-outdoors">Outdoors or exposed to direct rain/spray?</label>
        <div class="${NS}-field">
          <select id="${NS}-outdoors" aria-label="Outdoors or exposed">
            <option value="no">No</option>
            <option value="occasional">Occasional</option>
            <option value="routine">Routine / wind-driven</option>
          </select>
          <div class="${NS}-hint">Direct or wind‑driven water suggests Wet‑location.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-hose">Hose‑down / pressure‑wash?</label>
        <div class="${NS}-field">
          <select id="${NS}-hose" aria-label="Hose-down">
            <option value="none">None</option>
            <option value="light">Light / occasional</option>
            <option value="heavy">Frequent / high pressure</option>
          </select>
          <div class="${NS}-hint">Frequent wash‑down often needs IP66 and NEMA 4X.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-corrosion">Corrosive environment (coastal salt / chemicals)?</label>
        <div class="${NS}-field">
          <select id="${NS}-corrosion" aria-label="Corrosive environment">
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
          <div class="${NS}-hint">Corrosion resistance favors NEMA 4X materials/finish.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-temp">Coldest ambient (°F)</label>
        <div class="${NS}-field">
          <input id="${NS}-temp" type="number" step="1" value="40" aria-label="Coldest ambient Fahrenheit" style="width:120px">
          <div class="${NS}-hint">Below 32°F: low‑temp/heater packs; ≤ ‑4°F: consider remote battery indoors.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-haz">Hazardous location (flammable gas/dust)?</label>
        <div class="${NS}-field">
          <select id="${NS}-haz" aria-label="Hazardous classification">
            <option value="no">No</option>
            <option value="c1d2">Class I, Division 2</option>
            <option value="c1d1">Class I, Division 1</option>
            <option value="class2or3">Class II / III</option>
            <option value="unknown">Unknown / not sure</option>
          </select>
          <div class="${NS}-hint">Wet‑location ≠ explosion‑proof. Use proper Class/Division equipment if applicable.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-type">What are you specifying?</label>
        <div class="${NS}-field">
          <select id="${NS}-type" aria-label="Fixture type">
            <option value="combo">Exit + Lights (Combo)</option>
            <option value="light">Emergency Light</option>
            <option value="sign">Exit Sign</option>
            <option value="unsure">Unsure / decide later</option>
          </select>
          <div class="${NS}-hint">Combos simplify install over exterior doors; separate units for longer paths.</div>
        </div>
      </div>
    `;

    const actions = el("div", NS+"-actions");
    const btn = el("button", NS+"-btn", '<span class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></span> Get Recommendations');
    btn.type = "button";
    const reset = el("button", NS+"-btn secondary", '<span class="fa-solid fa-rotate" aria-hidden="true"></span> Reset');
    reset.type = "button";
    actions.appendChild(btn);
    actions.appendChild(reset);

    const result = el("div", NS+"-results");
    result.setAttribute("aria-live", "polite");
    result.innerHTML = `<p class="${NS}-muted">Your recommendations will appear here.</p>`;

    card.appendChild(form);
    card.appendChild(actions);
    card.appendChild(result);

    // Logic
    function compute(){
      const outdoors = form.querySelector('#'+NS+'-outdoors').value; // no | occasional | routine
      const hose = form.querySelector('#'+NS+'-hose').value; // none | light | heavy
      const corrosion = form.querySelector('#'+NS+'-corrosion').value; // no | yes
      const tempF = parseFloat(form.querySelector('#'+NS+'-temp').value || "40");
      const haz = form.querySelector('#'+NS+'-haz').value; // no | c1d2 | c1d1 | class2or3 | unknown
      const type = form.querySelector('#'+NS+'-type').value;

      // Rating (Dry/Damp/Wet)
      let rating = "Dry / Damp";
      if (outdoors !== "no" || hose !== "none") rating = "Wet Location";

      // IP
      let ip = "IP65";
      if (hose === "heavy" || outdoors === "routine") ip = "IP66";
      else if (outdoors !== "no" || hose === "light") ip = "IP65";

      // NEMA
      let nema = "NEMA 4";
      if (corrosion === "yes" || hose === "heavy") nema = "NEMA 4X";

      // Low temp
      let tempNote = null;
      if (tempF <= -4) tempNote = "Below −4°F: use low‑temperature/heater options or place battery indoors with outdoor remote heads.";
      else if (tempF < 32) tempNote = "Below 32°F: add low‑temperature/heater options to preserve 90‑minute runtime.";

      // Hazard
      let hazNote = null;
      if (haz !== "no") hazNote = "Hazardous location indicated — Wet‑location does not equal explosion‑proof. Specify correct Class/Division equipment.";

      // Testing
      const testNote = "Self‑diagnostics recommended for mounted‑high/outdoor units; ensure indicator visibility or remote test provision.";

      // Install notes
      const installNotes = [
        "Use liquid‑tight fittings; plug unused knockouts.",
        "Maintain correct orientation so weep/drain paths function.",
        "Add a drip loop on exterior cable to prevent water tracking.",
        "Clean mating surfaces; torque cover screws in a star pattern for even gasket compression.",
        "Prefer stainless hardware in coastal/chemical environments."
      ];

      // Fixture label
      const typeLabel = (type==="combo"?"Exit‑Light Combo": type==="light"?"Emergency Light": type==="sign"?"Exit Sign":"Fixture");

      // Render results
      const pills = [
        `<span class="${NS}-pill"><span class="fa-solid fa-shield-halved" aria-hidden="true"></span> ${rating}</span>`,
        `<span class="${NS}-pill"><span class="fa-solid fa-water" aria-hidden="true"></span> ${ip}</span>`,
        `<span class="${NS}-pill"><span class="fa-solid fa-toolbox" aria-hidden="true"></span> ${nema}</span>`,
        `<span class="${NS}-pill"><span class="fa-solid fa-clipboard-check" aria-hidden="true"></span> ${typeLabel}</span>`
      ].join(" ");

      let html = `<div>${pills}</div>`;
      html += `<ul class="${NS}-list">
        <li><strong>Runtime:</strong> UL 924 requires ≥90 minutes of emergency operation.</li>
        <li><strong>Testing:</strong> ${testNote}</li>
        <li><strong>Installation:</strong> ${installNotes[0]} ${installNotes[1]} ${installNotes[2]}</li>
        <li><strong>Gaskets:</strong> ${installNotes[3]}</li>
        <li><strong>Hardware:</strong> ${installNotes[4]}</li>
      </ul>`;

      if (tempNote) html += `<p><span class="${NS}-pill"><span class="fa-solid fa-snowflake" aria-hidden="true"></span> Cold weather</span> ${tempNote}</p>`;
      if (hazNote) html += `<p><span class="${NS}-pill"><span class="fa-solid fa-triangle-exclamation" aria-hidden="true"></span> Safety</span> ${hazNote}</p>`;

      // Copyable summary
      const summaryTxt = [
        `Recommendation: ${typeLabel} — ${rating}, ${nema}, ${ip}.`,
        tempNote ? `Cold-weather: ${tempNote}` : null,
        hazNote ? `Hazard: ${hazNote}` : null,
        `Notes: ${testNote} Install: ${installNotes.join(" ")}`
      ].filter(Boolean).join("\n");

      html += `<div class="${NS}-actions" style="margin-top:10px">
        <button type="button" class="${NS}-btn secondary" data-copy="${NS}">Copy Summary</button>
        <button type="button" class="${NS}-btn secondary" data-reset="${NS}">Clear</button>
      </div>
      <div class="${NS}-sr" aria-live="polite">Recommendation ready</div>`;

      result.innerHTML = html;

      const copyBtn = result.querySelector('[data-copy="'+NS+'"]');
      const clearBtn = result.querySelector('[data-reset="'+NS+'"]');
      if (copyBtn){
        copyBtn.addEventListener("click", ()=>{
          navigator.clipboard.writeText(summaryTxt).catch(()=>{});
          copyBtn.textContent = "Copied!";
          setTimeout(()=>{ copyBtn.textContent = "Copy Summary"; }, 1300);
        });
      }
      if (clearBtn){
        clearBtn.addEventListener("click", ()=>{
          result.innerHTML = `<p class="${NS}-muted">Your recommendations will appear here.</p>`;
        });
      }
    }

    btn.addEventListener("click", compute);
    reset.addEventListener("click", function(){
      form.reset();
      form.querySelector('#'+NS+'-temp').value = "40";
      result.innerHTML = `<p class="${NS}-muted">Your recommendations will appear here.</p>`;
    });

    root.appendChild(wrap);
  }

  function init(){
    injectCSS();
    const roots = document.querySelectorAll(SELECTOR);
    roots.forEach(root => {
      // guard against duplicate render
      if (root.__WL_ET_MOUNTED__) return;
      root.__WL_ET_MOUNTED__ = true;
      build(root);
    });
  }

  if (document.readyState === "loading"){
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();